/**
 * Rate Change Impact Page
 * Allows users to assess and initiate interest rate changes for variable-rate contracts.
 * Rate values come from Reference Data (pre-populated via URL params when navigating from Ref Data).
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  PlayArrow as InitiateIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { apiGet, apiPost, extractApiData } from '../utils/apiClient';
import useAsyncData from '../hooks/useAsyncData';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { usePermissions } from '../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import RateChangePreviewModal from '../components/contracts/RateChangePreviewModal';
import { useNotification } from '../contexts/NotificationContext';
import { describeBoundRate } from '../utils/rateBounds';

// T3-170: a contract's floor/ceiling held the proposed rate — say so beside it, with the
// benchmark + spread it replaced, so the sweep never presents a clamped rate as the raw one.
const BoundChip = ({ rate, uncapped }) => {
  const note = describeBoundRate(rate, uncapped);
  if (!note) return null;
  return (
    <Tooltip title={note} arrow>
      <Chip
        label={parseFloat(uncapped) > parseFloat(rate) ? 'At ceiling' : 'At floor'}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
      />
    </Tooltip>
  );
};

function RateChangeImpact() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useCurrencyFormatter();

  const canWrite = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.WRITE);

  // Form state - pre-populated from URL params when coming from Reference Data
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [selectedRateType, setSelectedRateType] = useState(searchParams.get('type') || '');
  const [reason, setReason] = useState('');
  const [benchmarkRateId, setBenchmarkRateId] = useState(searchParams.get('benchmark_id') || '');
  const [effectiveDate, setEffectiveDate] = useState(searchParams.get('date') || '');

  // Countries - loaded on mount
  const { data: countries } = useAsyncData(
    () => apiGet('/refdata/countries'),
    { defaultValue: [] }
  );

  // Rate types - reloaded when selectedCountry changes
  const { data: rateTypes } = useAsyncData(
    useCallback(() => apiGet(`/rate-changes/benchmark-types/${selectedCountry}`), [selectedCountry]),
    { defaultValue: [], fetchOnMount: !!selectedCountry, deps: [selectedCountry] }
  );

  // Rate comes from ref data (URL param or selected rate type)
  const selectedTypeData = rateTypes.find(t => t.interest_rate_type === selectedRateType);
  const newRate = searchParams.get('rate') || selectedTypeData?.latest_rate || '';

  // Results state
  const [impactResult, setImpactResult] = useState(null);
  const [initiateResult, setInitiateResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const notify = useNotification();

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, rateChangeId: null, currencyCode: 'ZAR' });

  // Auto-populate benchmark rate ID and effective date when rate type is selected
  useEffect(() => {
    const selectedType = rateTypes.find(t => t.interest_rate_type === selectedRateType);
    if (selectedType) {
      setBenchmarkRateId(selectedType.latest_rate_id);
      // Pre-populate effective date from ref data
      if (selectedType.effective_date) {
        const refDate = new Date(selectedType.effective_date);
        setEffectiveDate(refDate.toISOString().split('T')[0]);
      }
    }
  }, [selectedRateType, rateTypes]);

  const handlePreviewImpact = async () => {
    if (!selectedCountry || !selectedRateType || !newRate || !effectiveDate) {
      notify.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setImpactResult(null);

    try {
      const response = await apiPost('/rate-changes/impact-assessment', {
        interest_rate_type: selectedRateType,
        country_id: parseInt(selectedCountry),
        new_rate: parseFloat(newRate),
        effective_date: effectiveDate,
      });
      setImpactResult(extractApiData(response, null));
    } catch (err) {
      notify.error(err.message || 'Failed to load impact assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateChanges = async () => {
    if (!reason.trim()) {
      notify.error('Please provide a reason for the rate change');
      return;
    }

    setInitiating(true);

    try {
      const response = await apiPost('/rate-changes/initiate', {
        interest_rate_type: selectedRateType,
        country_id: parseInt(selectedCountry),
        new_rate: parseFloat(newRate),
        effective_date: effectiveDate,
        reason: reason.trim(),
        benchmark_rate_id: benchmarkRateId ? parseInt(benchmarkRateId) : null,
      });

      const initiatedCount = response?.data?.initiated_count || 0;
      const skippedCount = response?.data?.skipped_count || 0;
      notify.success(
        `${initiatedCount} rate change(s) submitted for approval` +
          (skippedCount > 0 ? `; ${skippedCount} contract(s) skipped — no change` : '') +
          '. Review them on the Approvals page.'
      );
      setInitiateResult(response?.data || null);
      setImpactResult(null);
      setConfirmDialog({ open: false, type: null, data: null });
    } catch (err) {
      notify.error(err.message || 'Failed to initiate rate changes');
    } finally {
      setInitiating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const RateChangeChip = ({ oldRate, newRate: newRateVal }) => {
    const change = parseFloat(newRateVal) - parseFloat(oldRate);
    const isIncrease = change > 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" color="textSecondary">
          {parseFloat(oldRate).toFixed(2)}%
        </Typography>
        {isIncrease ? (
          <TrendingUpIcon color="error" fontSize="small" />
        ) : (
          <TrendingDownIcon color="success" fontSize="small" />
        )}
        <Typography variant="body2" fontWeight="bold" color={isIncrease ? 'error.main' : 'success.main'}>
          {parseFloat(newRateVal).toFixed(2)}%
        </Typography>
        <Chip
          label={`${change >= 0 ? '+' : ''}${change.toFixed(2)}%`}
          size="small"
          color={isIncrease ? 'error' : 'success'}
          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Interest Rate Impact Assessment</Typography>
        <Button onClick={() => navigate('/approvals')}>
          View Approvals
        </Button>
      </Box>
      {/* Assessment Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Rate Change Parameters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a country and benchmark rate type to assess impact. Rate values are sourced from Reference Data.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Country / Currency</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  label="Country / Currency"
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.country} ({country.currency_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth disabled={!selectedCountry}>
                <InputLabel>Benchmark Rate Type</InputLabel>
                <Select
                  value={selectedRateType}
                  onChange={(e) => setSelectedRateType(e.target.value)}
                  label="Benchmark Rate Type"
                >
                  {rateTypes.map((type) => (
                    <MenuItem key={type.interest_rate_type} value={type.interest_rate_type}>
                      {type.interest_rate_type} (Current: {parseFloat(type.latest_rate).toFixed(2)}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <TextField
                fullWidth
                label="Effective Date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Pre-populated from Reference Data"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
                onClick={handlePreviewImpact}
                disabled={loading || !selectedCountry || !selectedRateType || !newRate || !effectiveDate}
                sx={{ height: '56px' }}
              >
                Preview Impact
              </Button>
            </Grid>
          </Grid>
          {newRate && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>New Rate:</strong> {parseFloat(newRate).toFixed(2)}% (from Reference Data)
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* Initiation Result (post-submit) */}
      {initiateResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6">Rate Change Submitted</Typography>
                <Typography variant="body2" color="textSecondary">
                  {initiateResult.initiated_count} contract(s) submitted for approval
                  {initiateResult.skipped_count > 0 && `; ${initiateResult.skipped_count} skipped (no change)`}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setInitiateResult(null)}>Dismiss</Button>
            </Box>
            {initiateResult.skipped_count > 0 && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  The following contract(s) were not updated because the new benchmark plus their spread
                  produces the same rate as their current rate.
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Contract</TableCell>
                        <TableCell>Investor</TableCell>
                        <TableCell align="right">Current Rate</TableCell>
                        <TableCell align="right">Spread</TableCell>
                        <TableCell align="right">Proposed Rate</TableCell>
                        <TableCell align="right">Capital</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {initiateResult.skipped_contracts?.map((c) => (
                        <TableRow key={c.contract_id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ cursor: 'pointer', color: 'primary.main' }}
                              onClick={() => navigate(`/contracts/${c.contract_id}`)}
                            >
                              {c.reference}
                            </Typography>
                          </TableCell>
                          <TableCell>{c.investor_name}</TableCell>
                          <TableCell align="right">{parseFloat(c.current_rate).toFixed(2)}%</TableCell>
                          <TableCell align="right">
                            {parseFloat(c.rate_spread) !== 0
                              ? `${parseFloat(c.rate_spread) >= 0 ? '+' : ''}${parseFloat(c.rate_spread).toFixed(2)}%`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {parseFloat(c.proposed_new_rate).toFixed(2)}%
                            <BoundChip rate={c.proposed_new_rate} uncapped={c.uncapped_new_rate} />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(c.capital_amount, c.currency_code)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="textSecondary">{c.reason}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}
      {/* Impact Results */}
      {impactResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Impact Assessment Results
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {impactResult.impacted_count} contract(s) will be affected
                  {impactResult.pending_count > 0 && ` (${impactResult.pending_count} already have pending changes)`}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="textSecondary">Total Capital Impacted</Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(impactResult.summary?.total_capital || 0)}
                </Typography>
              </Box>
            </Box>

            {impactResult.contracts?.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Contract</TableCell>
                        <TableCell>Investor</TableCell>
                        <TableCell align="right">Current Rate</TableCell>
                        <TableCell align="right">Spread</TableCell>
                        <TableCell align="right">New Rate</TableCell>
                        <TableCell align="right">Capital</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Maturity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {impactResult.contracts.map((contract) => (
                        <TableRow key={contract.contract_id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ cursor: 'pointer', color: 'primary.main' }}
                              onClick={() => navigate(`/contracts/${contract.contract_id}`)}
                            >
                              {contract.reference}
                            </Typography>
                          </TableCell>
                          <TableCell>{contract.investor_name}</TableCell>
                          <TableCell align="right">{parseFloat(contract.current_rate).toFixed(2)}%</TableCell>
                          <TableCell align="right">
                            {parseFloat(contract.rate_spread) !== 0 ? (
                              <Chip
                                label={`${parseFloat(contract.rate_spread) >= 0 ? '+' : ''}${parseFloat(contract.rate_spread).toFixed(2)}%`}
                                size="small"
                                color={parseFloat(contract.rate_spread) >= 0 ? 'info' : 'warning'}
                                sx={{ height: 20 }}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <RateChangeChip oldRate={contract.current_rate} newRate={contract.proposed_new_rate} />
                            <BoundChip rate={contract.proposed_new_rate} uncapped={contract.uncapped_new_rate} />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(contract.capital_amount, contract.currency_code)}</TableCell>
                          <TableCell>
                            {formatDate(contract.contract_effective_date || effectiveDate)}
                            {contract.rate_change_effective_basis === 'anniversary' && (
                              <Chip label="Anniversary" size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                            )}
                            {contract.rate_change_effective_basis === 'specified_date' && (
                              <Chip label="Specified" size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell>{formatDate(contract.maturity_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {canWrite && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      label="Reason for Rate Change"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="e.g., SARB MPC rate increase, JIBAR adjustment..."
                      required
                    />
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={initiating ? <CircularProgress size={20} /> : <InitiateIcon />}
                      onClick={() => setConfirmDialog({ open: true, type: 'initiate', data: impactResult })}
                      disabled={initiating || !reason.trim()}
                      sx={{ minWidth: 200, height: 56 }}
                    >
                      Initiate Changes
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">No contracts will be affected by this rate change.</Alert>
            )}
          </CardContent>
        </Card>
      )}
      {/* Confirm Initiate Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null, data: null })}>
        <DialogTitle>Confirm Rate Change Initiation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to initiate rate changes for {confirmDialog.data?.impacted_count || 0} contract(s).
            <br /><br />
            <strong>New Benchmark Rate:</strong> {newRate}%<br />
            <strong>Effective Date:</strong> {formatDate(effectiveDate)}<br />
            <strong>Reason:</strong> {reason}
            <br /><br />
            These contracts will be marked as "Pending Rate Change" and will require approval before the new rates are applied.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null, data: null })}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleInitiateChanges}
            disabled={initiating}
          >
            {initiating ? 'Initiating...' : 'Confirm Initiation'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Rate Change Preview Modal (read-only — approvals live on the Approvals page) */}
      <RateChangePreviewModal
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, rateChangeId: null, currencyCode: 'ZAR' })}
        rateChangeId={previewDialog.rateChangeId}
        currencyCode={previewDialog.currencyCode}
        showApprove={false}
      />
    </Box>
  );
}

export default RateChangeImpact;
